import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '@hooks/redux';
import { hideSessionTimeoutWarning } from '@store/slices/authSlice';
import authService from '@services/authService';

const SessionTimeoutWarning: React.FC = () => {
  const dispatch = useAppDispatch();
  const { sessionTimeoutWarning, tokenExpiresAt } = useAppSelector((state) => state.auth);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);

  useEffect(() => {
    if (!sessionTimeoutWarning || !tokenExpiresAt) {
      return;
    }

    const updateTimer = () => {
      const expiryTime = new Date(tokenExpiresAt).getTime();
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((expiryTime - now) / 1000));
      setTimeRemaining(remaining);

      if (remaining === 0) {
        dispatch(hideSessionTimeoutWarning());
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [sessionTimeoutWarning, tokenExpiresAt, dispatch]);

  const handleExtendSession = async () => {
    try {
      await authService.extendSession();
      dispatch(hideSessionTimeoutWarning());
    } catch (error) {
      console.error('Failed to extend session:', error);
    }
  };

  const handleLogout = () => {
    authService.performLogout();
  };

  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;

  return (
    <Dialog
      open={sessionTimeoutWarning}
      onClose={() => {}} // Prevent closing by clicking outside
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
          <WarningIcon color="warning" />
          <Typography variant="h6">Session Expiring Soon</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Your session will expire in{' '}
          <Box
            component="span"
            sx={{
              fontWeight: 'bold',
              color: 'error.main'
            }}>
            {minutes}:{seconds.toString().padStart(2, '0')}
          </Box>
        </Typography>
        <Typography variant="body2" sx={{
          color: 'text.secondary'
        }}>
          Would you like to extend your session?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleLogout}
          color="inherit"
        >
          Logout
        </Button>
        <Button
          onClick={handleExtendSession}
          variant="contained"
          color="primary"
        >
          Extend Session
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutWarning;
